import { h, Host } from '@stencil/core';
import debounce from 'lodash/debounce';
import { BUTTON_TYPES, myProductsResponseStatus, productCtasButtonType, send, productSelectorAddToCartEvent, isAnalyticsEnabled, tryParse, ConfiguratorHandler } from '@pes-ui/utils';
import { isNull } from 'lodash';
import { HIDE_TIMEOUT } from './constants';
import NotificationBannerController from '../UI/pes-notification-banner/controller';
import { addToMyProducts, deleteMyProducts, getMyProducts, scAddToCart, } from '../../globals/my-products/api';
import { timeLimitedRequest } from './utils';
import { isBrowser } from '../../globals/utils';
export class PesMyProductsConnector {
  constructor() {
    var _a;
    this.promiseRequest = null;
    this.myProductsGroupsCache = null;
    this.notificationElement = null;
    this.notificationController = new NotificationBannerController(HIDE_TIMEOUT);
    this.unifiedCheckoutFlag = (_a = ConfiguratorHandler.getSingleConfig('FeatureFlags')) === null || _a === void 0 ? void 0 : _a.unifiedCheckout;
    this.isUnifiedCheckoutEnabled = ConfiguratorHandler.isFeatureFlagEnabled(this.unifiedCheckoutFlag || {});
    this.handleUcoEvent = (configurationEvent) => {
      var _a, _b;
      const eventType = (_b = (_a = configurationEvent === null || configurationEvent === void 0 ? void 0 : configurationEvent.extras) === null || _a === void 0 ? void 0 : _a.addTo) !== null && _b !== void 0 ? _b : null;
      if (isNull(eventType))
        return;
      const checkoutEvent = eventType === productCtasButtonType.CART
        ? BUTTON_TYPES.ADD_TO_CART : BUTTON_TYPES.ADD_TO_MYLIST;
      const ucCtaEvent = new CustomEvent(checkoutEvent, {
        detail: {
          payload: configurationEvent,
          type: "sc",
        },
      });
      document.dispatchEvent(ucCtaEvent);
    };
    this.handleGetMyProducts = debounce(async () => {
      this.pesGetMyProductsSucceeded.emit(await this.getMyProducts());
    }, 200);
    this.handleScSelectorAddToCart = async (event) => {
      try {
        await this.handleScAddToCart(event.detail);
      }
      catch (error) {
        console.log('Error handling SC selector add to cart:', error);
      }
    };
    this.handleScAddToCart = async (configurationEvent) => {
      const response = await scAddToCart(this.localeCode, configurationEvent);
      const { status } = response;
      this.myProductsGroupsCache = null;
      if (isAnalyticsEnabled() && status === myProductsResponseStatus.SUCCESS) {
        this.sendScAnalytics(configurationEvent, response);
      }
      return this.showBanner("ADDED_TO_CART", 'body');
    };
    this.handleButtonClick = () => {
      window.open(this.myProductsURL, '_self');
    };
    this.localeCode = undefined;
    this.myProductsURL = undefined;
    this.labels = undefined;
    this.isExternalConsumer = undefined;
    this.externalMyProducts = undefined;
    this._localeCode = undefined;
    this._myProductsURL = undefined;
    this._labels = undefined;
    this._externalMyProducts = undefined;
  }
  async handleCtaDidClick(event) {
    const { type, product, bannerContext, onFinish, inList } = event.detail;
    let request;
    let notificationType;
    if (['favorites', 'cart'].includes(type)) {
      if (type === 'favorites' && inList) {
        request = this.handleRemoveFromList(product, onFinish);
        notificationType = "REMOVED_FROM_MY_PRODUCTS";
      }
      else {
        request = this.handleAddToList(product, onFinish);
        notificationType = type === 'favorites'
          ? "ADDED_TO_MY_PRODUCTS"
          : "ADDED_TO_CART";
      }
      const response = await request;
      if ((response === null || response === void 0 ? void 0 : response.status) === myProductsResponseStatus.SUCCESS) {
        this.showBanner(notificationType, bannerContext);
      }
    }
  }
  async handleSelectorAddToCart(event) {
    var _a, _b;
    if (!this.isUnifiedCheckoutEnabled || ((_b = (_a = event === null || event === void 0 ? void 0 : event.detail) === null || _a === void 0 ? void 0 : _a.extras) === null || _b === void 0 ? void 0 : _b.addTo) === undefined) {
      await this.handleScAddToCart(event.detail);
    }
    this.handleUcoEvent(event.detail);
  }
  componentWillLoad() {
    var _a, _b, _c, _d;
    this.localeCode = (_a = tryParse(this._localeCode)) !== null && _a !== void 0 ? _a : this.localeCode;
    this.myProductsURL = (_b = tryParse(this._myProductsURL)) !== null && _b !== void 0 ? _b : this.myProductsURL;
    this.labels = (_c = tryParse(this._labels)) !== null && _c !== void 0 ? _c : this.labels;
    this.externalMyProducts = (_d = tryParse(this._externalMyProducts)) !== null && _d !== void 0 ? _d : this.externalMyProducts;
  }
  connectedCallback() {
    document.addEventListener('pesGetMyProducts', this.handleGetMyProducts);
    window.addEventListener('scSelectorAddToCart', this.handleScSelectorAddToCart);
  }
  disconnectedCallback() {
    document.removeEventListener('pesGetMyProducts', this.handleGetMyProducts);
    window.removeEventListener('scSelectorAddToCart', this.handleScSelectorAddToCart);
  }
  attachListeners(el) {
    el === null || el === void 0 ? void 0 : el.addEventListener('actionDidClick', this.handleButtonClick);
  }
  async handleRemoveFromList(productData, onFinish) {
    const response = await timeLimitedRequest(deleteMyProducts)(this.localeCode, productData);
    if (typeof onFinish === 'function') {
      onFinish(response);
    }
    this.myProductsGroupsCache = null;
    return response;
  }
  async handleAddToList(productData, onFinish) {
    const response = await timeLimitedRequest(addToMyProducts)(this.localeCode, productData);
    if (typeof onFinish === 'function') {
      onFinish(response);
      this.myProductsGroupsCache = null;
    }
    return response;
  }
  sendScAnalytics(configurationEvent, response) {
    var _a, _b;
    const { productsUpdated, infoForGa } = response;
    const { products } = configurationEvent;
    const quantities = products.reduce((acc, product) => {
      const { productId, quantity } = product;
      acc[productId] = quantity;
      return acc;
    }, {});
    const mappedProducts = productsUpdated.map((id) => {
      const { description: name, brand = '', price = null } = infoForGa[id];
      return {
        id,
        brand,
        name,
        price,
        category: '',
        variant: '',
        quantity: quantities[id],
      };
    });
    const currencyCode = (_b = (_a = Object.values(infoForGa).find((product) => product.currency)) === null || _a === void 0 ? void 0 : _a.currency) !== null && _b !== void 0 ? _b : '';
    send(productSelectorAddToCartEvent, {
      currencyCode,
      distributor: '',
      products: mappedProducts,
    });
  }
  async showBanner(type, target) {
    const notificationElement = await this.notificationController.show({
      target,
      componentProps: this.getAllProps(type),
    });
    this.attachListeners(notificationElement);
  }
  getBannerContent(type) {
    switch (type) {
      case "ADDED_TO_CART":
        return {
          message: this.labels.addedToCart,
          btnMessage: this.labels.viewCart,
        };
      case "ADDED_TO_MY_PRODUCTS":
        return {
          message: this.labels.addedToMyProducts,
          btnMessage: this.labels.viewMyProducts,
          type,
        };
      case "REMOVED_FROM_MY_PRODUCTS":
        return {
          message: this.labels.removedFromMyProducts,
          type,
        };
      default:
        return {};
    }
  }
  getAllProps(type) {
    const actions = {};
    const bannerContent = this.getBannerContent(type);
    if (bannerContent === null || bannerContent === void 0 ? void 0 : bannerContent.btnMessage) {
      actions.primary = {
        label: bannerContent.btnMessage,
      };
    }
    return {
      active: true,
      status: "warning",
      actions,
      innerHTML: bannerContent.message,
    };
  }
  async getMyProducts() {
    var _a;
    if (this.isExternalConsumer && this.externalMyProducts) {
      this.myProductsGroupsCache = this.externalMyProducts || [];
      this.promiseRequest = null;
      return this.myProductsGroupsCache;
    }
    if (!this.myProductsGroupsCache && !window.isAsco) {
      this.promiseRequest = (_a = this.promiseRequest) !== null && _a !== void 0 ? _a : getMyProducts(this.localeCode);
      this.myProductsGroupsCache = await this.promiseRequest;
    }
    this.promiseRequest = null;
    return this.myProductsGroupsCache;
  }
  componentDidLoad() {
    if (isBrowser()) {
      this.getMyProducts();
    }
  }
  render() {
    return h(Host, null);
  }
  static get is() { return "pes-my-products-connector"; }
  static get encapsulation() { return "shadow"; }
  static get originalStyleUrls() {
    return {
      "$": ["pes-my-products-connector.scss"]
    };
  }
  static get styleUrls() {
    return {
      "$": ["pes-my-products-connector.css"]
    };
  }
  static get properties() {
    return {
      "localeCode": {
        "type": "string",
        "mutable": true,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "locale-code",
        "reflect": false
      },
      "myProductsURL": {
        "type": "string",
        "mutable": true,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "my-products-u-r-l",
        "reflect": false
      },
      "labels": {
        "type": "unknown",
        "mutable": true,
        "complexType": {
          "original": "{\n    addedToMyProducts: string;\n    addedToCart: string;\n    removedFromMyProducts: string;\n    viewMyProducts: string;\n    viewCart: string;\n  }",
          "resolved": "{ addedToMyProducts: string; addedToCart: string; removedFromMyProducts: string; viewMyProducts: string; viewCart: string; }",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        }
      },
      "isExternalConsumer": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "Defines consumer is external-user"
        },
        "attribute": "is-external-consumer",
        "reflect": false
      },
      "externalMyProducts": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "TGetMyProductsResult",
          "resolved": "TProductGroup[]",
          "references": {
            "TGetMyProductsResult": {
              "location": "import",
              "path": "@pes-ui/utils"
            }
          }
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "Defines list of favorite items for external-user"
        }
      },
      "_localeCode": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-locale-code",
        "reflect": false
      },
      "_myProductsURL": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-my-products-url",
        "reflect": false
      },
      "_labels": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-labels",
        "reflect": false
      },
      "_externalMyProducts": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-external-my-products",
        "reflect": false
      }
    };
  }
  static get events() {
    return [{
        "method": "pesGetMyProductsSucceeded",
        "name": "pesGetMyProductsSucceeded",
        "bubbles": true,
        "cancelable": true,
        "composed": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "complexType": {
          "original": "TGetMyProductsResult",
          "resolved": "TProductGroup[]",
          "references": {
            "TGetMyProductsResult": {
              "location": "import",
              "path": "@pes-ui/utils"
            }
          }
        }
      }];
  }
  static get listeners() {
    return [{
        "name": "ctaClick",
        "method": "handleCtaDidClick",
        "target": "document",
        "capture": false,
        "passive": false
      }, {
        "name": "scSelectorAddToCart",
        "method": "handleSelectorAddToCart",
        "target": "document",
        "capture": false,
        "passive": false
      }];
  }
}
